import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Image1 from "../../assets/logo.png";


const Sidebar = () => {
  const auth = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State for collapsed groups - default to expanded if pathname matches
  const [expandedGroups, setExpandedGroups] = useState({
    administration:
      pathname === "/audit-trail" ||
      pathname === "/select-staff-for-camp",
    reporting:
      pathname.startsWith("/report-events") ||
      pathname === "/report-dashboard" ||
      pathname === "/report-config",
    cooperatives:
      pathname === "/coop-management" ||
      pathname === "/coop-form-builder" ||
      pathname === "/coop-form" ||
      pathname === "/coop-progress" ||
      pathname === "/coop-form-audit-log",
    staff:
      pathname.startsWith("/staff") ||
      pathname === "/staff-schedule" ||
      pathname === "/staff-attendance",
    infants:
      pathname.startsWith("/enroll-infant") ||
      pathname.startsWith("/infants"),
    hallManagement:
      pathname.startsWith("/hall-assignment") ||
      pathname.startsWith("/hall-schedule"),
    accommodation: pathname.startsWith("/accommodation"),
    userManagement:
      pathname === "/update-user" ||
      pathname === "/update-image" ||
      pathname === "/update-finger-print" ||
      pathname === "/id-card",
  });

  const hasRoles = (roles = []) => roles.includes(auth?.userInfo?.role);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      // If the clicked group is already expanded, close it
      if (prev[groupName]) {
        return {
          ...prev,
          [groupName]: false,
        };
      }
      // Otherwise, close all groups and open the clicked one
      const allGroups = {
        administration: false,
        reporting: false,
        cooperatives: false,
        infants: false,
        hallManagement: false,
        health: false,
        accommodation: false,
        userManagement: false,
      };
      return {
        ...allGroups,
        [groupName]: true,
      };
    });
  };

  const handleLogout = () => {
    dispatch({
      type: "LOGOUT_SUCCESS",
    });
    localStorage.removeItem("token");
    navigate("/login");
    window.scrollTo(0, 0);
  };

  const isActive = (path) => {
    if (path === "/" && pathname === "/") return true;
    if (path !== "/" && pathname.startsWith(path)) return true;
    return false;
  };

  // Collapsible Group Component
  const CollapsibleGroup = ({ title, icon, isExpanded, onToggle, children, show = true }) => {
    if (!show) return null;

    return (
      <li className="sidebar__group">
        <button
          className={`sidebar__group-header ${isExpanded ? "sidebar__group-header--expanded" : ""}`}
          onClick={onToggle}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {icon && <span className="sidebar__icon">{icon}</span>}
            <span style={{ fontWeight: "500", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {title}
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="sidebar__icon"
            width="16"
            height="16"
            style={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {isExpanded && (
          <ul className="sidebar__group-items" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {children}
          </ul>
        )}
      </li>
    );
  };

  // Staff users only see the logout button
  const isStaffUser = auth?.userInfo?.role === "staff";
  // Test role only sees Test Scores page and logout (like food-checker limited to food)
  const isTestUser = auth?.userInfo?.role === "test";

  return (
    <aside className="sidebar">
          <img className="sidebar__logo" src={Image1} alt="" />
      <nav className="sidebar__nav">
        <ul className="sidebar__menu">
          {/* Staff users see My Tasks, My Profile and Logout */}
          {isStaffUser ? (
            <>
              <li className={pathname === "/coop-form" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                <Link to="/coop-form" className="sidebar__link">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  <span>My Forms</span>
                </Link>
              </li>
              <li className={pathname.startsWith("/report-events") ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                <Link to="/report-events" className="sidebar__link">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                  <span>My Tasks</span>
                </Link>
              </li>
              <li className={pathname.startsWith("/staff-profile") ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                <Link to={`/staff-profile/${auth?.userInfo?.id}`} className="sidebar__link">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>My Profile</span>
                </Link>
              </li>
              <li className="sidebar__menu-item">
                <button onClick={handleLogout} className="sidebar__link" style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  <span>Logout</span>
                </button>
              </li>
            </>
          ) : isTestUser ? (
            <>
              <li
                className={pathname === "/test-scores" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
              >
                <Link to="/test-scores" className="sidebar__link">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <span>Test Scores</span>
                </Link>
              </li>
              <li className="sidebar__menu-item">
                <button onClick={handleLogout} className="sidebar__link" style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="sidebar__icon"
                    width="20"
                    height="20"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  <span>Logout</span>
                </button>
              </li>
            </>
          ) : (
            <>

              {/* Registered Users - only for admin/super-admin (since it's not in the requirements, keeping it for admin only) */}
          {['admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/registered" || pathname === "/" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/registered" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Registered Users</span>
              </Link>
            </li>
          )}

          {/* Youth In Work - registered users with youthInWork flag; admin/super-admin */}
          {['admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/youth-in-work" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/youth-in-work" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                <span>Youth In Work</span>
              </Link>
            </li>
          )}

          {/* Work enabling outreach - Y1 ∪ (Y2 currently checked in); admin/super-admin */}
          {['admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/work-enabling-outreach" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/work-enabling-outreach" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span>Work enabling outreach</span>
              </Link>
            </li>
          )}

          {/* Attrition list - past cooperative members (live pastMembers); admin/super-admin */}
          {['admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/attrition-list" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/attrition-list" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="17" y1="8" x2="22" y2="13" />
                  <line x1="22" y1="8" x2="17" y2="13" />
                </svg>
                <span>Attrition list</span>
              </Link>
            </li>
          )}

          {/* Users - visible to: admin, super-admin, attendance, camp-checkin, accommodation */}
          {['admin', 'super-admin', 'attendance', 'camp-checkin', 'accommodation'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/users" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/users" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Users</span>
              </Link>
            </li>
          )}

          {/* Camp Status - visible to: admin, super-admin, camp-checkin */}
          {['camp-checkin', 'admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/camp-status" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/camp-status" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M3 21h18" />
                  <path d="M5 21V7" />
                  <path d="M19 21V10" />
                  <path d="M9 21V12" />
                  <rect x="7" y="3" width="6" height="4" rx="1" />
                  <rect x="15" y="5" width="4" height="4" rx="1" />
                </svg>
                <span>Camp Status</span>
              </Link>
            </li>
          )}

          {/* Food - visible to: admin, super-admin, food-checker */}
          {['admin', 'super-admin', 'food-checker'].includes(auth?.userInfo?.role) && (
          <li
              className={pathname === "/food" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
          >
            <Link to="/food" className="sidebar__link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sidebar__icon"
                width="20"
                height="20"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-1a7 7 0 0 1 7-7h2a7 7 0 0 1 7 7v1" />
                <path d="M5 3h14" />
                <path d="M7 3v4" />
                <path d="M17 3v4" />
              </svg>
              <span>Food</span>
            </Link>
          </li>
          )}

          {/* Test Scores - visible to: admin, super-admin */}
          {['admin', 'super-admin'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/test-scores" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/test-scores" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>Test Scores</span>
              </Link>
            </li>
          )}

          {/* Tablet Collection - visible to: admin, super-admin, tab-checking */}
          {['admin', 'super-admin', 'tab-checking'].includes(auth?.userInfo?.role) && (
            <li
              className={pathname === "/tablet-collection" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
            >
              <Link to="/tablet-collection" className="sidebar__link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                <span>Tablet Collection</span>
              </Link>
            </li>
          )}

        
              {/* Accommodation Group - visible to: admin, super-admin, accommodation */}
              {["accommodation", "admin", "super-admin"].includes(auth?.userInfo?.role) && (
                <CollapsibleGroup
                  title="Accommodation"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M3 7v10" />
                      <path d="M21 7v10" />
                      <path d="M3 12h18" />
                      <path d="M5 7h14a2 2 0 0 1 2 2v3h-2v5h-2v-5H7v5H5v-5H3V9a2 2 0 0 1 2-2Z" />
                    </svg>
                  }
                  isExpanded={expandedGroups.accommodation}
                  onToggle={() => toggleGroup("accommodation")}
                >
                  <li
                    className={pathname === "/accommodation" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}
                  >
                    <Link to="/accommodation" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M3 7v10" />
                  <path d="M21 7v10" />
                  <path d="M3 12h18" />
                  <path d="M5 7h14a2 2 0 0 1 2 2v3h-2v5h-2v-5H7v5H5v-5H3V9a2 2 0 0 1 2-2Z" />
                </svg>
                      <span>Rooms</span>
                    </Link>
                  </li>
                </CollapsibleGroup>
              )}

              {/* Staff Group - separate section */}
              {["staff-attendance", "accommodation", "admin", "super-admin"].includes(auth?.userInfo?.role) && (
                <CollapsibleGroup
                  title="Staff"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  }
                  isExpanded={expandedGroups.staff}
                  onToggle={() => toggleGroup("staff")}
                >
                  <li className={pathname === "/staff" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/staff" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="sidebar__icon"
                        width="20"
                        height="20"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      <span>Staff List</span>
                    </Link>
                  </li>
                  {hasRoles(["admin", "super-admin"]) && (
                    <li className={pathname === "/create-staff" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                      <Link to="/create-staff" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="sidebar__icon"
                          width="20"
                          height="20"
                        >
                          <path d="M12 5v14" />
                          <path d="M5 12h14" />
                        </svg>
                        <span>Add Staff</span>
                      </Link>
                    </li>
                  )}
                  {auth?.userInfo?.role !== "accommodation" && (
                    <li className={pathname === "/staff-schedule" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                      <Link to="/staff-schedule" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="sidebar__icon"
                          width="20"
                          height="20"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <span>Staff Schedule</span>
                      </Link>
                    </li>
                  )}
                  {auth?.userInfo?.role !== "accommodation" && (
                    <li className={pathname === "/staff-attendance" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                      <Link to="/staff-attendance" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="sidebar__icon"
                          width="20"
                          height="20"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>Staff Attendance</span>
                      </Link>
                    </li>
                  )}
                </CollapsibleGroup>
              )}

              {/* Administration Group */}
          {hasRoles(["admin", "super-admin"]) && (
                <CollapsibleGroup
                  title="Administration"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  }
                  isExpanded={expandedGroups.administration}
                  onToggle={() => toggleGroup("administration")}
                >
                  <li className={pathname === "/audit-trail" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/audit-trail" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Audit Trail</span>
              </Link>
            </li>
                  <li className={pathname === "/audit-trail" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/audit-trail" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Audit Trail</span>
              </Link>
            </li>
            <li className={pathname === "/select-staff-for-camp" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
              <Link to="/select-staff-for-camp" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sidebar__icon"
                  width="20"
                  height="20"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  <path d="M9 11l2 2 4-4" />
                </svg>
                <span>Select Staff for Camp</span>
              </Link>
            </li>
                </CollapsibleGroup>
              )}


              {/* Reporting Group - visible to: admin, super-admin, staff */}
              {["admin", "super-admin", "staff"].includes(auth?.userInfo?.role) && (
                <CollapsibleGroup
                  title="Tasks"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  }
                  isExpanded={expandedGroups.reporting}
                  onToggle={() => toggleGroup("reporting")}
                >
                  <li className={pathname.startsWith("/report-events") ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/report-events" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>Events</span>
                    </Link>
                  </li>
                  {hasRoles(["admin", "super-admin"]) && (
                    <li className={pathname === "/report-dashboard" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                      <Link to="/report-dashboard" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                          <path d="M3 3v18h18" />
                          <path d="M18 17V9" />
                          <path d="M13 17V5" />
                          <path d="M8 17v-3" />
                        </svg>
                        <span>Dashboard</span>
                      </Link>
                    </li>
                  )}
                  {hasRoles(["admin", "super-admin"]) && (
                    <li className={pathname === "/report-config" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                      <Link to="/report-config" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                        <span>Config</span>
                      </Link>
                    </li>
                  )}
                </CollapsibleGroup>
              )}

              {/* Cooperatives Group */}
              <CollapsibleGroup
                title="Cooperatives"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="16"
                    height="16"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                }
                isExpanded={expandedGroups.cooperatives}
                onToggle={() => toggleGroup("cooperatives")}
              >
                {/* <li className={pathname === "/coop-form" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                  <Link to="/coop-form" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span>My Forms</span>
                  </Link>
                </li> */}
                {hasRoles(["admin", "super-admin"]) && (
                  <li className={pathname === "/coop-management" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/coop-management" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      <span>Manage</span>
                    </Link>
                  </li>
                )}
                {hasRoles(["admin", "super-admin"]) && (
                  <li className={pathname === "/coop-form-builder" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/coop-form-builder" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      <span>Form Builder</span>
                    </Link>
                  </li>
                )}
                {hasRoles(["admin", "super-admin"]) && (
                  <li className={pathname === "/coop-progress" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/coop-progress" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                        <path d="M3 3v18h18" />
                        <path d="M18 17V9" />
                        <path d="M13 17V5" />
                        <path d="M8 17v-3" />
                      </svg>
                      <span>Progress</span>
                    </Link>
                  </li>
                )}
                {hasRoles(["admin", "super-admin"]) && (
                  <li className={pathname === "/coop-form-audit-log" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/coop-form-audit-log" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar__icon" width="20" height="20">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                        <path d="M9 12h6" />
                        <path d="M9 16h6" />
                      </svg>
                      <span>Form Audit Log</span>
                    </Link>
                  </li>
                )}
              </CollapsibleGroup>

              {/* Infants Group - visible to: admin, super-admin, creche */}
              {hasRoles(["admin", "super-admin", "creche"]) && (
                <CollapsibleGroup
                  title="Infants"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  }
                  isExpanded={expandedGroups.infants}
                  onToggle={() => toggleGroup("infants")}
                >
                  <li className={pathname === "/enroll-infant" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/enroll-infant" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="sidebar__icon"
                        width="20"
                        height="20"
                      >
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                      <span>Enroll Infant</span>
                    </Link>
                  </li>
                  <li className={pathname === "/infants" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/infants" className="sidebar__link" style={{ paddingLeft: "32px" }}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sidebar__icon"
                width="20"
                height="20"
              >
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
                      <span>Infants</span>
            </Link>
          </li>
                </CollapsibleGroup>
              )}

              {/* Hall Management Group */}
              {hasRoles(["admin", "super-admin"]) && (
                <CollapsibleGroup
                  title="Hall Management"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M9 9h6v6H9z" />
                    </svg>
                  }
                  isExpanded={expandedGroups.hallManagement}
                  onToggle={() => toggleGroup("hallManagement")}
                >
                  <li className={pathname === "/hall-assignment" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/hall-assignment" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="sidebar__icon"
                        width="20"
                        height="20"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M9 9h6v6H9z" />
                      </svg>
                      <span>Hall Assignment</span>
                    </Link>
                  </li>
                  <li className={pathname === "/hall-schedule" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/hall-schedule" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="sidebar__icon"
                        width="20"
                        height="20"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>Hall Schedule</span>
            </Link>
          </li>
                </CollapsibleGroup>
              )}

              {/* Health Group - visible to health, admin, and super-admin */}
              {hasRoles(["health", "admin", "super-admin"]) && (
                <CollapsibleGroup
                  title="Health"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>Health</title>
                      <rect width="24" height="24" fill="none" strokeWidth="0" opacity="0.01" />
                      <path d="M17.5,13.5h-4v4h-3v-4h-4v-3h4v-4h3v4h4ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Z" transform="translate(0 0)" />
                    </svg>
                  }
                  isExpanded={expandedGroups.health}
                  onToggle={() => toggleGroup("health")}
                >
                  <li className={pathname === "/health" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/health" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <title>Health</title>
                        <rect width="24" height="24" fill="none" strokeWidth="0" opacity="0.01" />
                        <path d="M17.5,13.5h-4v4h-3v-4h-4v-3h4v-4h3v4h4ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Z" transform="translate(0 0)" />
                      </svg>
                      <span>Record Appointment</span>
                    </Link>
                  </li>
                  <li className={(pathname === "/medical-inventory" || pathname.startsWith("/medical-inventory/")) ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                    <Link to="/medical-inventory" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="sidebar__icon"
                        width="20"
                        height="20"
                      >
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      <span>Medical Inventory</span>
                    </Link>
                  </li>
                </CollapsibleGroup>
              )}

              {/* User Management Group - visible only to admin and super-admin */}
              {hasRoles(["admin", "super-admin"]) && (
                <CollapsibleGroup
                  title="User Management"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="16"
                      height="16"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  }
                  isExpanded={expandedGroups.userManagement}
                  onToggle={() => toggleGroup("userManagement")}
                >
                <li className={pathname === "/update-user" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                  <Link to="/update-user" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                    <svg className="sidebar__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.5 19.5L20 21M11 21H5.6C5.03995 21 4.75992 21 4.54601 20.891C4.35785 20.7951 4.20487 20.6422 4.10899 20.454C4 20.2401 4 19.9601 4 19.4V17.6841C4 17.0485 4 16.7306 4.04798 16.4656C4.27087 15.2344 5.23442 14.2709 6.46558 14.048C6.5425 14.0341 6.6237 14.0242 6.71575 14.0172C6.94079 14 7.05331 13.9914 7.20361 14.0026C7.35983 14.0143 7.4472 14.0297 7.59797 14.0722C7.74302 14.1131 8.00429 14.2315 8.52682 14.4682C9.13692 14.7446 9.8015 14.9218 10.5 14.9795M19 17.5C19 18.8807 17.8807 20 16.5 20C15.1193 20 14 18.8807 14 17.5C14 16.1193 15.1193 15 16.5 15C17.8807 15 19 16.1193 19 17.5ZM15 7C15 9.20914 13.2091 11 11 11C8.79086 11 7 9.20914 7 7C7 4.79086 8.79086 3 11 3C13.2091 3 15 4.79086 15 7Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Update personal info</span>
                  </Link>
                </li>
                <li className={pathname === "/update-image" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                  <Link to="/update-image" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                    <svg className="sidebar__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5535 2.49392C12.4114 2.33852 12.2106 2.25 12 2.25C11.7894 2.25 11.5886 2.33852 11.4465 2.49392L7.44648 6.86892C7.16698 7.17462 7.18822 7.64902 7.49392 7.92852C7.79963 8.20802 8.27402 8.18678 8.55352 7.88108L11.25 4.9318V16C11.25 16.4142 11.5858 16.75 12 16.75C12.4142 16.75 12.75 16.4142 12.75 16V4.9318L15.4465 7.88108C15.726 8.18678 16.2004 8.20802 16.5061 7.92852C16.8118 7.64902 16.833 7.17462 16.5535 6.86892L12.5535 2.49392Z" />
                      <path d="M3.75 15C3.75 14.5858 3.41422 14.25 3 14.25C2.58579 14.25 2.25 14.5858 2.25 15V15.0549C2.24998 16.4225 2.24996 17.5248 2.36652 18.3918C2.48754 19.2919 2.74643 20.0497 3.34835 20.6516C3.95027 21.2536 4.70814 21.5125 5.60825 21.6335C6.47522 21.75 7.57754 21.75 8.94513 21.75H15.0549C16.4225 21.75 17.5248 21.75 18.3918 21.6335C19.2919 21.5125 20.0497 21.2536 20.6517 20.6516C21.2536 20.0497 21.5125 19.2919 21.6335 18.3918C21.75 17.5248 21.75 16.4225 21.75 15.0549V15C21.75 14.5858 21.4142 14.25 21 14.25C20.5858 14.25 20.25 14.5858 20.25 15C20.25 16.4354 20.2484 17.4365 20.1469 18.1919C20.0482 18.9257 19.8678 19.3142 19.591 19.591C19.3142 19.8678 18.9257 20.0482 18.1919 20.1469C17.4365 20.2484 16.4354 20.25 15 20.25H9C7.56459 20.25 6.56347 20.2484 5.80812 20.1469C5.07435 20.0482 4.68577 19.8678 4.40901 19.591C4.13225 19.3142 3.9518 18.9257 3.85315 18.1919C3.75159 17.4365 3.75 16.4354 3.75 15Z" />
                    </svg>
              <span>Upload photo and credentials</span>
            </Link>
          </li>
                <li className={pathname === "/update-finger-print" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                  <Link to="/update-finger-print" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                    <svg className="sidebar__icon" width="20" height="20" viewBox="0 0 192 192" xmlns="http://www.w3.org/2000/svg" fill="none">
                      <path strokeWidth="4" d="M140.424 38.019a3.6 3.6 0 0 1-1.777-.462C123.81 29.934 110.983 26.7 95.528 26.7c-15.223 0-29.75 3.619-42.964 10.857-1.854 1.001-4.172.308-5.254-1.54-1.005-1.848-.31-4.235 1.545-5.236C63.228 22.85 78.992 19 95.528 19c16.537 0 30.91 3.619 46.673 11.55 1.932 1.155 2.628 3.465 1.623 5.313-.695 1.386-1.932 2.156-3.4 2.156ZM29.846 78.444a4.036 4.036 0 0 1-2.24-.693c-1.624-1.232-2.165-3.619-.928-5.39 7.65-10.78 17.386-19.25 28.977-25.179 24.419-12.474 55.328-12.551 79.669-.077 11.591 5.929 21.327 14.245 28.977 25.025 1.237 1.694.773 4.158-.927 5.39-1.777 1.232-4.173.847-5.409-.77-6.955-9.856-15.764-17.479-26.196-22.792-22.177-11.319-50.536-11.319-72.636.077-10.51 5.39-19.319 13.09-26.273 22.715-.618 1.155-1.778 1.694-3.014 1.694Zm48.296 92.939c-1.005 0-1.932-.385-2.705-1.155-6.722-6.699-10.354-11.011-15.532-20.328-5.332-9.471-8.113-21.021-8.113-33.418 0-22.869 19.627-41.503 43.736-41.503 24.11 0 43.737 18.634 43.737 41.503 0 1.021-.407 2-1.132 2.722a3.87 3.87 0 0 1-5.464 0 3.844 3.844 0 0 1-1.131-2.722c0-18.634-16.15-33.803-36.01-33.803-19.859 0-36.01 15.169-36.01 33.803 0 11.088 2.474 21.329 7.187 29.568 4.946 8.932 8.346 12.705 14.296 18.711a3.943 3.943 0 0 1 0 5.467c-.927.77-1.855 1.155-2.86 1.155Zm55.405-14.245c-9.196 0-17.309-2.31-23.955-6.853-11.514-7.777-18.391-20.405-18.391-33.803 0-1.021.407-2 1.132-2.722a3.871 3.871 0 0 1 5.464 0 3.843 3.843 0 0 1 1.131 2.722c0 10.857 5.564 21.098 14.991 27.412 5.487 3.696 11.9 5.467 19.628 5.467 1.854 0 4.945-.231 8.036-.77 2.087-.385 4.173 1.001 4.482 3.157.386 2.002-1.005 4.081-3.168 4.466-4.405.847-8.268.924-9.35.924ZM118.015 173h-1.005c-12.286-3.542-20.323-8.085-28.745-16.324-10.819-10.626-16.769-24.948-16.769-40.194 0-12.474 10.664-22.638 23.8-22.638 13.137 0 23.801 10.164 23.801 22.638 0 8.239 7.341 14.938 16.072 14.938 8.887 0 16.073-6.699 16.073-14.938 0-29.029-25.114-52.591-56.023-52.591-21.945 0-42.191 12.166-51.077 31.031-3.014 6.237-4.56 13.552-4.56 21.56 0 6.006.541 15.477 5.178 27.797.773 2.002-.232 4.235-2.241 4.928-2.01.693-4.25-.308-4.946-2.233-3.863-10.087-5.64-20.174-5.64-30.492 0-9.24 1.777-17.633 5.254-24.948 10.277-21.483 33.073-35.42 58.032-35.42 35.082 0 63.751 27.027 63.751 60.291 0 12.474-10.664 22.638-23.801 22.638-13.136 0-23.8-10.164-23.8-22.638 0-8.239-7.186-14.938-16.073-14.938-8.886 0-16.072 6.699-16.072 14.938 0 13.167 5.1 25.487 14.45 34.727 7.341 7.238 14.373 11.242 25.268 14.168 2.086.616 3.246 2.772 2.705 4.774-.387 1.771-2.009 2.926-3.632 2.926Z" />
                    </svg>
              <span>Upload fingerprint</span>
            </Link>
          </li>
                <li className={pathname === "/id-card" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
                  <Link to="/id-card" className="sidebar__link" style={{ paddingLeft: "32px" }}>
                    <svg className="sidebar__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5535 16.5061C12.4114 16.6615 12.2106 16.75 12 16.75C11.7894 16.75 11.5886 16.6615 11.4465 16.5061L7.44648 12.1311C7.16698 11.8254 7.18822 11.351 7.49392 11.0715C7.79963 10.792 8.27402 10.8132 8.55352 11.1189L11.25 14.0682V3C11.25 2.58579 11.5858 2.25 12 2.25C12.4142 2.25 12.75 2.58579 12.75 3V14.0682L15.4465 11.1189C15.726 10.8132 16.2004 10.792 16.5061 11.0715C16.8118 11.351 16.833 11.8254 16.5535 12.1311L12.5535 16.5061Z" />
                      <path d="M3.75 15C3.75 14.5858 3.41422 14.25 3 14.25C2.58579 14.25 2.25 14.5858 2.25 15V15.0549C2.24998 16.4225 2.24996 17.5248 2.36652 18.3918C2.48754 19.2919 2.74643 20.0497 3.34835 20.6516C3.95027 21.2536 4.70814 21.5125 5.60825 21.6335C6.47522 21.75 7.57754 21.75 8.94513 21.75H15.0549C16.4225 21.75 17.5248 21.75 18.3918 21.6335C19.2919 21.5125 20.0497 21.2536 20.6517 20.6516C21.2536 20.0497 21.5125 19.2919 21.6335 18.3918C21.75 17.5248 21.75 16.4225 21.75 15.0549V15C21.75 14.5858 21.4142 14.25 21 14.25C20.5858 14.25 20.25 14.5858 20.25 15C20.25 16.4354 20.2484 17.4365 20.1469 18.1919C20.0482 18.9257 19.8678 19.3142 19.591 19.591C19.3142 19.8678 18.9257 20.0482 18.1919 20.1469C17.4365 20.2484 16.4354 20.25 15 20.25H9C7.56459 20.25 6.56347 20.2484 5.80812 20.1469C5.07435 20.0482 4.68577 19.8678 4.40901 19.591C4.13225 19.3142 3.9518 18.9257 3.85315 18.1919C3.75159 17.4365 3.75 16.4354 3.75 15Z" />
                    </svg>
              <span>Download ID card</span>
            </Link>
          </li>
                </CollapsibleGroup>
              )}


          {/* Identify by Fingerprint - available to all roles */}
          <li className={pathname === "/identify-fingerprint" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
            <Link to="/identify-fingerprint" className="sidebar__link">
              <svg className="sidebar__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11.24V7.5C9 5.01 11.01 3 13.5 3C15.99 3 18 5.01 18 7.5V11.24C18.61 11.24 19.11 11.74 19.11 12.35V19.89C19.11 20.5 18.61 21 18 21H9C8.39 21 7.89 20.5 7.89 19.89V12.35C7.89 11.74 8.39 11.24 9 11.24ZM10.5 11.24H16.5V7.5C16.5 5.84 15.16 4.5 13.5 4.5C11.84 4.5 10.5 5.84 10.5 7.5V11.24Z" fill="currentColor"/>
                <path d="M12 2C10.34 2 9 3.34 9 5V7H7V5C7 2.24 9.24 0 12 0C14.76 0 17 2.24 17 5V7H15V5C15 3.34 13.66 2 12 2Z" fill="currentColor"/>
              </svg>
              <span>Identify by Fingerprint</span>
            </Link>
          </li>

          {/* Scan ID Card - available to all roles */}
          <li className={pathname === "/scan-id-card" ? "sidebar__menu-item sidebar__menu-item--active" : "sidebar__menu-item"}>
            <Link to="/scan-id-card" className="sidebar__link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sidebar__icon"
                width="20"
                height="20"
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
              <span>Scan ID Card</span>
            </Link>
          </li>

          <li className="sidebar__menu-item">
            <button onClick={handleLogout} className="sidebar__link" style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="sidebar__icon"
                width="20"
                height="20"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Logout</span>
            </button>
          </li>
            </>
          )}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
